import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AdminRole } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatCard } from "@/components/stat-card";
import { StatusBadge } from "@/components/status-badge";
import { formatUsdAmount } from "@/components/amount";
import { hasRole } from "@/lib/roles";
import {
  clearWithdrawalLimitOverride,
  getDefaultWithdrawalLimit,
  getResolvedWithdrawalLimit,
  setWithdrawalLimitOverride,
  updateDefaultWithdrawalLimit,
} from "@/api/withdrawal-limits";
import { ApiError } from "@/api/client";
import { type UpdateWithdrawalLimitInput, type WithdrawalLimitItem } from "@/api/types";

// Feature 030 Task 9. Nav renamed and page re-scoped to "Withdraw Limits"
// (was a general "Limits" home for every platform-wide cap the backoffice
// might ever govern) — withdrawals are the only limit family that exists
// today, so a page title that already says so makes the old per-family
// eyebrow header (icon + "Withdrawal limits" + description, directly under
// a page header saying the same thing) pure duplication. Dropped in favor
// of rendering `PlatformDefaultCard`/`MerchantOverrideCard` directly. A
// future limit family (e.g. deposits, refunds) most likely belongs on its
// own page/nav item rather than a same-page sibling section now that the
// title itself is family-specific — `SectionCard`/`EditLimitDialog`/
// `LimitTrio` below are still generic enough to reuse there either way.
//
// Read (`GET`) is FINANCE_VIEWER OR CONFIG_MANAGER, write
// (`PATCH`/`PUT`/`DELETE`) is CONFIG_MANAGER-only — same `canManage` split as
// _app.gas-station.tsx, gating only the edit/override/clear controls, not
// the whole page.

export const Route = createFileRoute("/_app/withdrawal-limits")({
  component: LimitsPage,
});

const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";

interface LimitFieldValues {
  minUsd: string;
  maxPerTxUsd: string;
  max24hUsd: string;
}

function toFieldValues(item: WithdrawalLimitItem): LimitFieldValues {
  return { minUsd: item.minUsd, maxPerTxUsd: item.maxPerTxUsd, max24hUsd: item.max24hUsd };
}

/** All three fields must be present and numeric — unlike gas-station's EditFloorDialog, there is no "leave empty to clear" convention here (every field is always required per spec). */
function validateFields(values: LimitFieldValues): string | null {
  for (const [label, raw] of [
    ["Minimum", values.minUsd],
    ["Max per transaction", values.maxPerTxUsd],
    ["Max per 24h", values.max24hUsd],
  ] as const) {
    const trimmed = raw.trim();
    if (trimmed === "") return `${label} is required.`;
    if (Number.isNaN(Number(trimmed))) return `${label} must be a number.`;
  }
  return null;
}

// ─── shared building blocks ─────────────────────────────────────────────────
//
// SectionCard mirrors _app.merchants_.$merchantId.tsx's local component of
// the same name/shape (title + description + optional top-right action +
// children) — this codebase's established per-route pattern for a titled
// card rather than a shared cross-route import.

function SectionCard({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="text-sm">{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        {action}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

/** The three limit fields, always shown together — an override "replaces all three values together" (spec), never one field at a time, so the display treats them as one unit too. */
function LimitTrio({ item }: { item: WithdrawalLimitItem }) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <StatCard label="Minimum" value={formatUsdAmount(item.minUsd)} />
      <StatCard label="Per-transaction cap" value={formatUsdAmount(item.maxPerTxUsd)} />
      <StatCard label="24-hour cap" value={formatUsdAmount(item.max24hUsd)} />
    </div>
  );
}

// ─── AML screening toggle (feature-036) ─────────────────────────────────────
//
// Platform-default only — `amlScreeningEnabled` is meaningless on a
// per-merchant override row (see `WithdrawalLimitItem`'s doc comment), so
// this only ever renders inside `PlatformDefaultCard`. The checkbox is bound
// directly to the loaded `item.amlScreeningEnabled`, never a separate local
// "optimistic" boolean — on a 409 refusal the mutation's `onSuccess` never
// fires, `item` is never touched, and the checkbox therefore falls straight
// back to its previous state by construction, with the server's own reason
// string surfaced verbatim (and left on screen, not a toast that vanishes)
// so the admin can act on it. Sends the currently-loaded three limit fields
// unchanged alongside the toggle — `PATCH /v1/withdrawal-limits` always
// requires them together.
function ScreeningToggle({
  item,
  canManage,
  onUpdate,
}: {
  item: WithdrawalLimitItem;
  canManage: boolean;
  onUpdate: (updated: WithdrawalLimitItem) => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (next: boolean) =>
      updateDefaultWithdrawalLimit({
        minUsd: item.minUsd,
        maxPerTxUsd: item.maxPerTxUsd,
        max24hUsd: item.max24hUsd,
        amlScreeningEnabled: next,
      }),
    onSuccess: (res) => {
      setError(null);
      onUpdate(res.data);
      toast.success(res.data.amlScreeningEnabled ? "AML screening enabled." : "AML screening disabled.");
    },
    onError: (err) => {
      // Deliberately NOT a generic fallback message for the common case: a
      // refused-enable 409 carries the actual reason (provider not
      // configured, its rules are empty, etc.) — that reason is the whole
      // point of the guard, so it is shown to the admin as-is.
      setError(err instanceof ApiError ? err.message : "Failed to update AML screening.");
    },
  });

  return (
    <div className="space-y-2 border-t border-[#E5E7EB] pt-4">
      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          className="h-4 w-4 cursor-pointer rounded border-[#D1D5DB] accent-[#0F172A] disabled:cursor-not-allowed"
          checked={item.amlScreeningEnabled}
          disabled={!canManage || mutation.isPending}
          onChange={(e) => {
            setError(null);
            mutation.mutate(e.target.checked);
          }}
        />
        AML screening enabled
      </label>
      <p className="text-xs text-muted-foreground">
        When enabled, every withdrawal destination is screened by the compliance provider before
        payout, holding a flagged or unscreenable destination for an AML officer's review. Enabling
        can be refused if the provider isn&apos;t ready to screen right now.
      </p>
      {error && <div className={ERROR_BOX}>{error}</div>}
    </div>
  );
}

// ─── edit dialog (shared by both the default and an override) ──────────────
//
// Mirrors _app.gas-station.tsx's EditFloorDialog shape: controlled useState
// form, inline ApiError.message error box, invalidate/update + toast on
// success — but three always-required numeric-string fields instead of one
// optional one.

interface EditLimitDialogProps {
  triggerLabel: string;
  title: string;
  description: string;
  initial: WithdrawalLimitItem;
  onSave: (input: UpdateWithdrawalLimitInput) => Promise<WithdrawalLimitItem>;
  onSuccess: (item: WithdrawalLimitItem) => void;
}

function EditLimitDialog({ triggerLabel, title, description, initial, onSave, onSuccess }: EditLimitDialogProps) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<LimitFieldValues>(() => toFieldValues(initial));
  const [error, setError] = useState<string | null>(null);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setValues(toFieldValues(initial));
      setError(null);
    }
  };

  const mutation = useMutation({
    mutationFn: () =>
      onSave({
        minUsd: values.minUsd.trim(),
        maxPerTxUsd: values.maxPerTxUsd.trim(),
        max24hUsd: values.max24hUsd.trim(),
      }),
    onSuccess: (item) => {
      toast.success("Withdrawal limits updated.");
      onSuccess(item);
      setOpen(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to update withdrawal limits.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const validationError = validateFields(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {error && <div className={ERROR_BOX}>{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="limit-min-usd">Minimum (USD)</Label>
            <Input
              id="limit-min-usd"
              value={values.minUsd}
              onChange={(e) => setValues((v) => ({ ...v, minUsd: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="limit-max-per-tx-usd">Max per transaction (USD)</Label>
            <Input
              id="limit-max-per-tx-usd"
              value={values.maxPerTxUsd}
              onChange={(e) => setValues((v) => ({ ...v, maxPerTxUsd: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="limit-max-24h-usd">Max per 24h (USD)</Label>
            <Input
              id="limit-max-24h-usd"
              value={values.max24hUsd}
              onChange={(e) => setValues((v) => ({ ...v, max24hUsd: e.target.value }))}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} className="w-full sm:w-auto">
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── platform default ────────────────────────────────────────────────────────

function PlatformDefaultCard({ canManage }: { canManage: boolean }) {
  const queryClient = useQueryClient();
  const { data, isPending, isError } = useQuery({
    queryKey: ["withdrawal-limits", "default"],
    queryFn: getDefaultWithdrawalLimit,
  });

  const item = data?.data ?? null;

  return (
    <SectionCard
      title="Platform default"
      description="Applies to every merchant without an override."
      action={
        canManage && item ? (
          <EditLimitDialog
            triggerLabel="Edit default"
            title="Edit platform default limits"
            description="Applies to every merchant that has no override of their own."
            initial={item}
            onSave={(input) => updateDefaultWithdrawalLimit(input).then((res) => res.data)}
            onSuccess={(updated) => queryClient.setQueryData(["withdrawal-limits", "default"], { data: updated })}
          />
        ) : undefined
      }
    >
      {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
      {isError && <p className="text-sm text-destructive">Failed to load the platform default.</p>}
      {item && (
        <>
          <LimitTrio item={item} />
          <ScreeningToggle
            item={item}
            canManage={canManage}
            onUpdate={(updated) => queryClient.setQueryData(["withdrawal-limits", "default"], { data: updated })}
          />
        </>
      )}
    </SectionCard>
  );
}

// ─── merchant override ──────────────────────────────────────────────────────

function MerchantOverrideCard({ canManage }: { canManage: boolean }) {
  const [merchantIdInput, setMerchantIdInput] = useState("");
  const [loadedMerchantId, setLoadedMerchantId] = useState<string | null>(null);
  const [resolved, setResolved] = useState<WithdrawalLimitItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    const trimmed = merchantIdInput.trim();
    if (trimmed === "") return;
    setLoading(true);
    setError(null);
    setResolved(null);
    getResolvedWithdrawalLimit(trimmed)
      .then((res) => {
        setResolved(res.data);
        setLoadedMerchantId(trimmed);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Failed to load merchant limits.");
        setLoadedMerchantId(null);
      })
      .finally(() => setLoading(false));
  };

  const clearMutation = useMutation({
    mutationFn: () => {
      if (!loadedMerchantId) throw new Error("No merchant loaded.");
      return clearWithdrawalLimitOverride(loadedMerchantId);
    },
    onSuccess: () => {
      toast.success("Override cleared — this merchant now uses the platform default.");
      if (loadedMerchantId) {
        // Reload the resolved view so it now reflects the (now-cleared) default.
        getResolvedWithdrawalLimit(loadedMerchantId).then((res) => setResolved(res.data));
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to clear override.");
    },
  });

  // The response's `merchantId` field tells us whether this row is an active
  // override (non-null, matches the requested merchant) or the resolved
  // platform default (null) — per the API client's own doc comment.
  const isOverrideActive = resolved?.merchantId !== null && resolved?.merchantId !== undefined;

  return (
    <SectionCard
      title="Merchant override"
      description="Look up a merchant's resolved limits — their override, or the platform default."
    >
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={merchantIdInput}
          onChange={(e) => setMerchantIdInput(e.target.value)}
          placeholder="Merchant ID"
          className="w-72"
          onKeyDown={(e) => {
            if (e.key === "Enter") load();
          }}
        />
        <Button type="button" variant="outline" size="sm" onClick={load} disabled={merchantIdInput.trim() === ""}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load"}
        </Button>
      </div>

      {error && <div className={ERROR_BOX}>{error}</div>}

      {resolved && loadedMerchantId && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <StatusBadge
              label={isOverrideActive ? "Override active" : "Using platform default"}
              // "warning" for an active override: it's a deliberate deviation
              // from platform-wide policy for one merchant, worth an admin's
              // notice — not a failure, but not the calm baseline state
              // either. "success" for the baseline (no deviation to track).
              color={isOverrideActive ? "warning" : "success"}
            />
            {canManage && (
              <div className="flex gap-2">
                <EditLimitDialog
                  triggerLabel="Set override"
                  title={`Set override — merchant ${loadedMerchantId}`}
                  description="Replaces this merchant's effective limits. Clear the override later to fall back to the platform default."
                  initial={resolved}
                  onSave={(input) => setWithdrawalLimitOverride(loadedMerchantId, input).then((res) => res.data)}
                  onSuccess={(updated) => setResolved(updated)}
                />
                {isOverrideActive && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" disabled={clearMutation.isPending}>
                        {clearMutation.isPending ? "Clearing…" : "Clear override"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Clear this merchant&apos;s override?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This merchant will revert to the platform default withdrawal limits. You can
                          set a new override at any time.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => clearMutation.mutate()}>
                          Clear override
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            )}
          </div>
          <LimitTrio item={resolved} />
        </div>
      )}
    </SectionCard>
  );
}

// ─── withdraw limits page ────────────────────────────────────────────────────

function LimitsPage() {
  const { admin } = Route.useRouteContext();
  const canManage = hasRole(admin.roles, AdminRole.CONFIG_MANAGER);

  return (
    <div className="space-y-4">
      <PlatformDefaultCard canManage={canManage} />
      <MerchantOverrideCard canManage={canManage} />
    </div>
  );
}
